import i18n from 'i18next';
import { useSyncExternalStore } from 'react';
import { initReactI18next } from 'react-i18next';

import { readStored, writeStored } from '@/utils/storage.ts';

import de, { type Translations } from './locales/de.ts';
import en from './locales/en.ts';

export const LANGUAGES = ['de', 'en'] as const;
export type Language = (typeof LANGUAGES)[number];

// Used for react-aria's I18nProvider, which formats numbers, currencies and dates.
export const LOCALES: Record<Language, string> = {
    de: 'de-DE',
    en: 'en-GB',
};

const STORAGE_KEY = 'language';

const isLanguage = (value: unknown): value is Language => LANGUAGES.includes(value as Language);

// Without a stored choice, the browser's language.
const readLanguage = (): Language => {
    const stored = readStored(STORAGE_KEY);
    if (isLanguage(stored)) return stored;
    return navigator.language.startsWith('de') ? 'de' : 'en';
};

export const getLanguage = (): Language => (isLanguage(i18n.language) ? i18n.language : 'de');

const subscribeToLanguage = (onChange: () => void) => {
    i18n.on('languageChanged', onChange);
    return () => i18n.off('languageChanged', onChange);
};

// Reading i18n.language during render isn't reactive (and the React Compiler memoizes it), so subscribe explicitly. The
// same in Node, where the start page is prerendered (web/prerender.mjs).
export const useLanguage = () => useSyncExternalStore(subscribeToLanguage, getLanguage, getLanguage);

export const setLanguage = (language: Language) => {
    writeStored(STORAGE_KEY, language);
    void i18n.changeLanguage(language);
};

i18n.on('languageChanged', (language) => {
    // Also loaded in Node, where the start page is prerendered (web/prerender.mjs), which has no document.
    if (typeof document !== 'undefined') document.documentElement.lang = language;
});

void i18n.use(initReactI18next).init({
    resources: {
        de: { translation: de },
        en: { translation: en },
    },
    lng: readLanguage(),
    fallbackLng: 'de',
    interpolation: {
        // React already escapes rendered strings.
        escapeValue: false,
    },
});

declare module 'i18next' {
    interface CustomTypeOptions {
        resources: {
            translation: Translations;
        };
    }
}

export default i18n;
